import { Link } from "@inertiajs/react";
import { useState, useRef, useEffect } from "react";
import { AlertCircle, ArrowLeft, CheckCircle, FileText, Loader2, Send, Upload } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import SuggestionsPanel, { Suggestion } from "./SuggestionsPanel";

interface CandidateSummary {
  id: number;
  full_name: string;
  email: string | null;
  practice_area: string;
  candidate_type: string;
  jd_year: number | null;
  firm_name: string;
}

interface Props {
  candidate: CandidateSummary;
  resume_text: string | null;
  parse_error: string | null;
  has_resume: boolean;
  has_optimized_resume: boolean;
  candidate_email: string | null;
  default_send_body: string;
  ai_assist: boolean;
}

type Mode = "editing" | "loading" | "reviewing";
type SendStatus = "idle" | "sending" | "sent" | "error";

interface SuggestionState { [id: string]: boolean | null }

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

export default function ResumeOptimizerShow({
  candidate, resume_text, parse_error, has_resume,
  has_optimized_resume, candidate_email, default_send_body, ai_assist,
}: Props) {
  const [resumeText, setResumeText]       = useState(resume_text ?? "");
  const [jdText, setJdText]               = useState("");
  const [jdOpen, setJdOpen]               = useState(false);
  const [mode, setMode]                   = useState<Mode>("editing");
  const [apiError, setApiError]           = useState<string | null>(null);
  const [suggestions, setSuggestions]     = useState<Suggestion[]>([]);
  const [flags, setFlags]                 = useState<string[]>([]);
  const [suggStates, setSuggStates]       = useState<SuggestionState>({});
  const [saveStatus, setSaveStatus]       = useState<"idle" | "saving" | "saved">("idle");

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendBody, setSendBody]           = useState(default_send_body);
  const [ccSender, setCcSender]           = useState(true);
  const [sendStatus, setSendStatus]       = useState<SendStatus>("idle");
  const [sendError, setSendError]         = useState<string | null>(null);

  const lastFocusRef = useRef<Element | null>(null);
  const modalRef     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sendModalOpen) {
      (lastFocusRef.current as HTMLElement | null)?.focus?.();
      return;
    }
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && sendStatus !== "sending") {
        setSendModalOpen(false);
        return;
      }
      if (e.key === "Tab") {
        const els = Array.from(
          modalRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          ) ?? []
        );
        if (!els.length) return;
        const first = els[0];
        const last  = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [sendModalOpen, sendStatus]);

  const basePath = `/candidates/${candidate.id}/resume-optimizer`;

  const canSend = has_optimized_resume && !!candidate_email;

  function openSendModal() {
    lastFocusRef.current = document.activeElement;
    setSendBody(default_send_body);
    setSendStatus("idle");
    setSendError(null);
    setSendModalOpen(true);
  }

  async function sendToCandidate() {
    setSendStatus("sending");
    setSendError(null);
    try {
      const res = await fetch(`${basePath}/send`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken(), "Accept": "application/json" },
        body:    JSON.stringify({ body: sendBody, cc_sender: ccSender }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: "Send failed" }));
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      setSendStatus("sent");
    } catch (e: unknown) {
      setSendStatus("error");
      setSendError(e instanceof Error ? e.message : "Send failed");
    }
  }

  async function runOptimizer() {
    setMode("loading");
    setApiError(null);

    try {
      const res = await fetch(`${basePath}/optimize`, {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          "X-CSRF-Token":  getCsrfToken(),
          "Accept":        "application/json",
        },
        body: JSON.stringify({ resume_text: resumeText, jd_text: jdText }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Server error" }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }

      const data = await res.json();
      const suggs: Suggestion[] = data.suggestions ?? [];
      const initialStates: SuggestionState = {};
      suggs.forEach((s) => { initialStates[s.id] = null; });

      setSuggestions(suggs);
      setFlags(data.flags ?? []);
      setSuggStates(initialStates);
      setMode("reviewing");
    } catch (e: unknown) {
      setApiError(e instanceof Error ? e.message : "Unknown error");
      setMode("editing");
    }
  }

  function acceptSuggestion(id: string)  { setSuggStates((prev) => ({ ...prev, [id]: true })); }
  function rejectSuggestion(id: string)  { setSuggStates((prev) => ({ ...prev, [id]: false })); }
  function acceptAll() { setSuggStates((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, true]))); }
  function rejectAll() { setSuggStates((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, false]))); }

  const acceptedTexts = suggestions
    .filter((s) => suggStates[s.id] === true)
    .map((s) => s.suggested);

  const pendingCount = suggestions.filter((s) => suggStates[s.id] === null).length;

  const finalResumeText = suggestions.reduce((text, s) => {
    if (suggStates[s.id] === true) return text.replace(s.original, s.suggested);
    return text;
  }, resumeText);

  async function saveToProfile() {
    setSaveStatus("saving");
    try {
      const res = await fetch(`${basePath}/save`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken(), "Accept": "application/json" },
        body: JSON.stringify({ final_text: finalResumeText }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Save failed");
      setSaveStatus("saved");
    } catch (e: unknown) {
      setApiError(e instanceof Error ? e.message : "Save failed");
      setSaveStatus("idle");
    }
  }

  function submitExportForm(path: string) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${basePath}/${path}`;
    const fields: Record<string, string> = {
      authenticity_token: getCsrfToken(),
      final_text:         finalResumeText,
    };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden"; input.name = name; input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  return (
    <AppLayout title="Resume Optimizer">
      <Link href={`/candidates/${candidate.id}`} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> {candidate.full_name}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resume Optimizer</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {[candidate.candidate_type, candidate.practice_area, candidate.jd_year ? `JD ${candidate.jd_year}` : null]
              .filter(Boolean).join(" · ")}
          </p>
        </div>
        {mode === "reviewing" && (
          <button
            onClick={() => setMode("editing")}
            className="text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-3 py-1.5"
          >
            ← Back to Resume
          </button>
        )}
      </div>

      {/* Alerts */}
      {parse_error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-700">Could not parse resume</p>
            <p className="text-xs text-red-500 mt-0.5">{parse_error}</p>
          </div>
        </div>
      )}
      {!has_resume && !parse_error && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <Upload className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            Upload a resume on the{" "}
            <Link href={`/candidates/${candidate.id}/package`} className="underline">package page</Link>
            , then return here.
          </p>
        </div>
      )}
      {apiError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{apiError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main panel */}
        <div className="lg:col-span-2 space-y-4">
          {mode !== "reviewing" ? (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold text-gray-800">Resume Text</Label>
                  {has_resume && resume_text && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Parsed from PDF
                    </span>
                  )}
                </div>
                <Textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  rows={24}
                  className="font-mono text-xs leading-relaxed"
                  placeholder={has_resume ? "Resume text will appear here..." : "Paste resume text here..."}
                />
                <p className="text-xs text-gray-400 mt-2">You can edit this text before running the optimizer.</p>
              </div>

              {/* JD targeting */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <button
                  onClick={() => setJdOpen(!jdOpen)}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  <span>Target Job Description <span className="text-gray-400 font-normal">(optional)</span></span>
                  <span className="text-xs text-gray-400">{jdOpen ? "▲ Hide" : "▼ Show"}</span>
                </button>
                {jdOpen && (
                  <div className="mt-3">
                    <Textarea
                      value={jdText}
                      onChange={(e) => setJdText(e.target.value)}
                      rows={6}
                      placeholder="Paste the target job description here to tailor optimization..."
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">Bullets will be reframed to align with this JD.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <SuggestionsPanel
                suggestions={suggestions}
                flags={flags}
                states={suggStates}
                onAccept={acceptSuggestion}
                onReject={rejectSuggestion}
                onAcceptAll={acceptAll}
                onRejectAll={rejectAll}
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Run / Export */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              {mode === "reviewing" ? "Export" : "Optimizer"}
            </p>

            {mode === "reviewing" ? (
              <div className="space-y-2">
                {pendingCount > 0 && (
                  <p className="text-xs text-amber-600 mb-2">{pendingCount} suggestion{pendingCount !== 1 ? "s" : ""} still pending</p>
                )}
                <Button
                  onClick={saveToProfile}
                  disabled={saveStatus === "saving" || saveStatus === "saved"}
                  className="w-full bg-green-700 hover:bg-green-800 text-white text-sm"
                >
                  {saveStatus === "saving" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</>
                    : saveStatus === "saved" ? "✓ Saved to Profile"
                    : "Save Optimized Resume"}
                </Button>
                <Button variant="outline" className="w-full text-sm" onClick={() => submitExportForm("export/pdf")}>
                  <FileText className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <Button variant="outline" className="w-full text-sm" onClick={() => submitExportForm("export/docx")}>
                  <FileText className="w-4 h-4 mr-1.5" /> Download DOCX
                </Button>
                <button
                  onClick={() => setMode("editing")}
                  className="w-full text-xs text-gray-400 hover:text-gray-600 py-1"
                >
                  Run optimizer again
                </button>
              </div>
            ) : ai_assist ? (
              <div className="space-y-2">
                <Button
                  onClick={runOptimizer}
                  disabled={!resumeText.trim() || mode === "loading"}
                  className="w-full bg-green-700 hover:bg-green-800 text-white"
                >
                  {mode === "loading"
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyzing...</>
                    : "Run Optimizer"
                  }
                </Button>
                <p className="text-xs text-gray-400 text-center">
                  Only rewrites existing content — no fabrication
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-400">Enable AI Assist in Settings to use the optimizer.</p>
            )}
          </div>

          {/* Send to candidate */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Send</p>
            <Button
              variant="outline"
              className="w-full text-sm"
              onClick={openSendModal}
              disabled={!canSend}
            >
              <Send className="w-4 h-4 mr-1.5" /> Send to Candidate
            </Button>
            {!has_optimized_resume && (
              <p className="text-xs text-gray-400 mt-2">Save the optimized resume first.</p>
            )}
            {has_optimized_resume && !candidate_email && (
              <p className="text-xs text-amber-600 mt-2">Candidate has no email address.</p>
            )}
          </div>

          {/* Candidate info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Candidate</p>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="font-medium text-gray-800">{candidate.full_name}</div>
              {candidate.firm_name && <div>{candidate.firm_name}</div>}
              {candidate.practice_area && <div>{candidate.practice_area}</div>}
              {candidate.jd_year && <div>JD {candidate.jd_year}</div>}
            </div>
          </div>

          {/* Rules */}
          {mode !== "reviewing" && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Optimization Rules</p>
              <ul className="space-y-1.5 text-xs text-gray-500">
                <li>• No new facts or experiences added</li>
                <li>• Action verb + subject + context + impact</li>
                <li>• Lead with strength, quantify where possible</li>
                <li>• 6-second scannable format</li>
                <li>• JD mirroring if job description provided</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Send modal */}
      {sendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => sendStatus !== "sending" && setSendModalOpen(false)} />
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="send-modal-title"
            className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6"
          >
            <h2 id="send-modal-title" className="text-base font-semibold text-gray-900 mb-1">Send Resume to Candidate</h2>
            <p className="text-xs text-gray-500 mb-4">
              Sending to <span className="font-medium text-gray-700">{candidate_email}</span>
            </p>

            {sendStatus === "sent" ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
                <p className="text-sm font-medium text-gray-800">Sent to {candidate.full_name}</p>
                <Button variant="outline" className="mt-2" onClick={() => setSendModalOpen(false)}>
                  Close
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <Label className="text-xs font-medium text-gray-700 mb-1.5 block">Message</Label>
                  <Textarea
                    value={sendBody}
                    onChange={(e) => setSendBody(e.target.value)}
                    rows={7}
                    className="text-sm"
                    disabled={sendStatus === "sending"}
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 mb-4 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={ccSender}
                    onChange={(e) => setCcSender(e.target.checked)}
                    disabled={sendStatus === "sending"}
                    className="rounded border-gray-300"
                  />
                  Cc me on this email
                </label>

                {sendStatus === "error" && sendError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-700">{sendError}</p>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setSendModalOpen(false)}
                    disabled={sendStatus === "sending"}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendToCandidate}
                    disabled={sendStatus === "sending" || !sendBody.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {sendStatus === "sending"
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending...</>
                      : <><Send className="w-4 h-4 mr-1.5" /> Send</>
                    }
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
